<script setup lang="ts">
import { computed } from 'vue'
import type { LibraryFolder } from './library_types'
import { useUserStore } from '@/authentication/user.store'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import PopupMenu from '@/components/PopupMenu.vue'
import type { MenuItemType } from '@/components/dropdown-menu/types'

const props = defineProps({
  folder: {
    type: Object as () => LibraryFolder,
    required: true
  },
  isArchiveMode: {
    type: Boolean,
    default: false
  },
  shouldShowPremiumAction: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['open', 'rename', 'share', 'contributors', 'archive', 'unarchive', 'delete', 'premium-action', 'removeSelfAsContributor'])

const visibilityStore = useVisibilityStore()
const { shareVisibility } = storeToRefs(visibilityStore)
const userStore = useUserStore()

const folderRoute = computed(() => {
  return '/library/folder/' + encodeURIComponent(props.folder.uuid)
})

const isFolderOwner = computed(() => userStore.userId === props.folder.owner && userStore.userId != null && props.folder.owner != null)

const menuItems = computed((): MenuItemType[] => {
  if (!props.isArchiveMode) {
    const menuItems: MenuItemType[] = [
      {
        label: 'Open',
        isRouterLink: true,
        route: folderRoute.value,
        action: () => emit('open', props.folder)
      }
    ]
    if(isFolderOwner.value) {
      menuItems.push({
        label: 'Rename',
        action: () => emit('rename', props.folder)
      })
    }
    if (getShareSubmenuItems.value.length > 0) {
      menuItems.push({
        label: 'Share',
        submenuItems: getShareSubmenuItems.value
      })
    } else if (shareVisibility.value) {
      menuItems.push({
        label: 'Share',
        action: () => emit('share', props.folder)
      })
    }
    if(isFolderOwner.value) {
      menuItems.push(
      {
        label: 'Archive',
        action: () => {
          if (props.shouldShowPremiumAction) {
            emit('premium-action')
          } else {
            emit('archive', props.folder)
          }
        }
      })
      menuItems.push({
        label: 'Delete',
        action: () => emit('delete', props.folder)
      })
      }
    return menuItems
  } else {
    return [
      {
        label: 'Unarchive',
        action: () => emit('unarchive', props.folder)
      },
      {
        label: 'Delete',
        action: () => emit('delete', props.folder)
      }
    ]
  }
})

const getShareSubmenuItems = computed((): MenuItemType[] => {
  const items: MenuItemType[] = []
  
  if (isFolderOwner.value) {
    items.push({
      label: 'Add Contributors',
      action: () => {
        if (props.shouldShowPremiumAction) {
          emit('premium-action')
        } else {
          emit('contributors', props.folder)
        }
      }
    })
  } else if (!isFolderOwner.value) {
    items.push({
      label: 'Remove me as a contributor',
      action: () => emit('removeSelfAsContributor', props.folder)
    })
  }
  
  if (shareVisibility.value) {
    items.push({
      label: 'Send a view-only link',
      action: () => emit('share', props.folder)
    })
  }
  
  return items
})
</script>

<template>
  <div>
    <PopupMenu 
      :items="menuItems" 
      triggerIcon="more_horiz" 
      triggerAriaLabel="Folder actions" 
      align="end" 
    />
  </div>
</template>
